import { customElement, html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import type { TemplateResult } from '@inter-ikea-kompis/web-component';

import DragAndDropAreaEventEnum from '../../enums/DragAndDropAreaEventEnum';
import type IPointerPosition from '../../types/IPointerPosition';

/**
 * Drag and Drop Area component.
 *
 * @event pointerMove Triggered when pointer is moving over the drop area.
 * @event pointerDown Triggered when pointer is down over the drop area.
 * @event pointerUp Triggered when pointer is up over the drop area.
 * @event enterDropArea Triggered when pointer has moved over the drop area.
 * @event leaveDropArea Triggered when pointer has left the drop area.
 */
@customElement('kompis-drag-and-drop-area')
export default class DragAndDropArea extends KompisElement {
	// Set dragged state.
	@property({ type: Boolean })
	public disableInput = false;

	// Id
	@property({ type: String })
	public areaId: string | null = null;

	private dropAreaRect: DOMRect | null;
	private resizeObserver = new ResizeObserver(this.onResize.bind(this));
	private isOverDropAreaState = false;
	private dropArea: HTMLElement | null;

	/**
	 * Renders the component.
	 *
	 * @returns Template.
	 */
	public render(): TemplateResult {
		return html`
			<style>
				:host {
					display: flex;
					flex-grow: 1;
					max-width: 100%;
					height: 100%;
				}
				.container {
					width: 100%;
				}
			</style>
			<div class="container" @slotchange=${this.getDropArea.bind(this)}>
				<slot></slot>
			</div>
		`;
	}

	/**
	 * Triggered when the element is connected.
	 */
	public connectedCallback(): void {
		super.connectedCallback();
		this.getDropArea();
		// Observers
		this.resizeObserver.observe(this);
		// Move events
		this.addEventListener('mousemove', this.pointerMove.bind(this));
		this.addEventListener('touchmove', this.onTouchMove.bind(this));
		// Pointer down events
		this.addEventListener('mousedown', this.pointerDown.bind(this));
		this.addEventListener('touchstart', this.onTouchDown.bind(this));
		// Pointer up events
		this.addEventListener('mouseup', this.pointerUp.bind(this));
		this.addEventListener('touchend', this.onTouchUp.bind(this));
	}

	/**
	 * Triggered when the component is removed from DOM.
	 */
	public disconnectedCallback(): void {
		super.disconnectedCallback();
		// Observers
		this.resizeObserver.unobserve(this);
		// Move events
		this.removeEventListener('mousemove', this.pointerMove.bind(this));
		this.removeEventListener('touchmove', this.onTouchMove.bind(this));
		// Pointer down events
		this.removeEventListener('mousedown', this.pointerDown.bind(this));
		this.removeEventListener('touchstart', this.onTouchDown.bind(this));
		// Pointer up events
		this.removeEventListener('mouseup', this.pointerUp.bind(this));
		this.removeEventListener('touchend', this.onTouchUp.bind(this));
	}

	/**
	 * Handle mousemove events.
	 *
	 * @param event PointerEvent.
	 */
	private pointerMove(event: PointerEvent): void {
		const position: IPointerPosition = { x: null, y: null };
		position.x = event.clientX;
		position.y = event.clientY;
		const isOverDropArea = this.isOverDropArea(position);
		if (!this.disableInput) {
			this.dispatchEvent(
				new CustomEvent(DragAndDropAreaEventEnum.pointerMove, {
					bubbles: true,
					detail: { position, isOverDropArea }
				})
			);
		}
	}

	/**
	 * Handle touchmove events.
	 *
	 * @param event TouchEvent.
	 */
	private onTouchMove(event: TouchEvent): void {
		const position: IPointerPosition = { x: null, y: null };
		position.x = event.touches.length && event.touches[0].clientX;
		position.y = event.touches.length && event.touches[0].clientY;
		const isOverDropArea = this.isOverDropArea(position);
		if (!this.disableInput) {
			this.dispatchEvent(
				new CustomEvent(DragAndDropAreaEventEnum.pointerMove, {
					bubbles: true,
					detail: { position, isOverDropArea }
				})
			);
		}
	}

	/**
	 * Handle mousedown events.
	 *
	 * @param e PointerEvent.
	 * @param event
	 */
	private pointerDown(event: PointerEvent): void {
		const position: IPointerPosition = { x: null, y: null };
		position.x = event.clientX;
		position.y = event.clientY;
		if (!this.disableInput) {
			this.dispatchEvent(
				new CustomEvent(DragAndDropAreaEventEnum.pointerDown, {
					bubbles: true,
					detail: {
						position,
						isOverDropArea: this.isOverDropArea(position)
					}
				})
			);
		}
	}

	/**
	 * Handle touchend events.
	 *
	 * @param event TouchEvent.
	 */
	private onTouchDown(event: TouchEvent): void {
		const position: IPointerPosition = { x: null, y: null };
		position.x = event.touches.length && event.touches[0].clientX;
		position.y = event.touches.length && event.touches[0].clientY;
		if (!this.disableInput) {
			this.dispatchEvent(
				new CustomEvent(DragAndDropAreaEventEnum.pointerDown, {
					bubbles: true,
					detail: {
						position,
						isOverDropArea: this.isOverDropArea(position)
					}
				})
			);
		}
	}

	/**
	 * Handle mouseup events.
	 *
	 * @param event PointerEvent.
	 */
	private pointerUp(event: PointerEvent): void {
		const position: IPointerPosition = { x: null, y: null };
		position.x = event.clientX;
		position.y = event.clientY;
		if (!this.disableInput) {
			this.dispatchEvent(
				new CustomEvent(DragAndDropAreaEventEnum.pointerUp, {
					bubbles: true,
					detail: {
						position,
						isOverDropArea: this.isOverDropArea(position)
					}
				})
			);
		}
	}

	/**
	 * Handle touchend events.
	 *
	 * @param event TouchEvent.
	 */
	private onTouchUp(event: TouchEvent): void {
		const position: IPointerPosition = { x: null, y: null };
		position.x = event.changedTouches.length && event.changedTouches[0].clientX;
		position.y = event.changedTouches.length && event.changedTouches[0].clientY;
		if (!this.disableInput) {
			this.dispatchEvent(
				new CustomEvent(DragAndDropAreaEventEnum.pointerUp, {
					bubbles: true,
					detail: {
						position,
						isOverDropArea: this.isOverDropArea(position)
					}
				})
			);
		}
	}

	/**
	 * Recalculate area size and position on resize.
	 */
	private onResize(): void {
		this.dropAreaRect = this.dropArea?.getBoundingClientRect() ?? null;
	}

	/**
	 * Get drop area.
	 */
	private getDropArea(): void {
		this.dropArea = this.querySelector('#' + this.areaId);
	}

	/**
	 * @param position
	 */
	private checkIsOverDropArea(position: IPointerPosition): boolean {
		if (!position?.y || !position?.x || !this.dropAreaRect) {
			return false;
		}
		return (
			position.y >= this.dropAreaRect.top - (window.scrollY || window.pageYOffset) &&
			position.y <= this.dropAreaRect.bottom - (window.scrollY || window.pageYOffset) &&
			position.x >= this.dropAreaRect.left &&
			position.x <= this.dropAreaRect.right
		);
	}

	/**
	 * Checks if pointer is over drop area and emits events.
	 *
	 * @param position IPointerPosition.
	 *
	 * @returns Boolean.
	 */
	private isOverDropArea(position: IPointerPosition): boolean {
		if (!this.dropAreaRect) {
			this.onResize();
			this.reconnectObserver();
		}

		if (this.checkIsOverDropArea(position)) {
			if (!this.isOverDropAreaState) {
				this.isOverDropAreaState = true;
				this.dispatchEvent(
					new CustomEvent(DragAndDropAreaEventEnum.enterDropArea, {
						bubbles: true,
						detail: { position }
					})
				);
			}
			return true;
		} else {
			if (this.isOverDropAreaState) {
				this.isOverDropAreaState = false;
				this.dispatchEvent(
					new CustomEvent(DragAndDropAreaEventEnum.leaveDropArea, {
						bubbles: true,
						detail: { position }
					})
				);
			}
			return false;
		}
	}

	/**
	 * Reconnects resize observer.
	 */
	private reconnectObserver(): void {
		this.resizeObserver.unobserve(this);
		this.resizeObserver.observe(this);
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'kompis-drag-and-drop-area': DragAndDropArea;
	}
}
